'use client';

import { Button, Typography } from '@curology/ui-components-web/react';
import CircleCheckIcon from '@curology/ui-components-web/react/Icon/icons/CircleCheckIcon';
import { type AxiosError } from 'axios';
import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import { post, type ValidationErrorData } from '@/utils/http/client';

import beforeAndAfterImage from './assets/before_and_after.png';
import { ProductCard } from './productCard';
import { useNavigation } from '../../hooks/useNavigation';

const features = [
  'Helps treat acne, dark spots, and texture',
  'Visibly blurs imperfections, evens out texture, and minimizes the look of pores',
  'Designed to wear under makeup',
];

const jsonHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

export const ClientContainer = () => {
  const { onNext } = useNavigation();
  const [open] = useState(false);
  const [processing, setProcessing] = useState(false);
  const flash = useFlashMessages();

  useEffect(() => {
    trackEvent(AnalyticsEvent.UPSELL_VIEWED, {
      upsell: 'DAY_PRIME_RX_UPSELL',
    });
  }, []);

  const acceptUpsell = () => {
    setProcessing(true);
    post<{ notification: { title: string; message: string } }>(
      '/api/patient/upsells',
      { upsell: 'DAY_PRIME_RX_UPSELL', accepted: true },
      { headers: jsonHeaders },
    )
      .then((response) => {
        flash.success({
          title: response.data.notification.title || 'Success!',
          message: response.data.notification.message,
        });
        trackEvent(AnalyticsEvent.UPSELL_ACCEPTED, {
          upsell: 'DAY_PRIME_RX_UPSELL',
        });
        onNext();
      })
      .catch((error: AxiosError<ValidationErrorData>) => {
        flash.error({
          title: 'Sorry!',
          message:
            error.response?.data.message ||
            'An error occurred while processing your request.',
        });
        trackEvent(AnalyticsEvent.UPSELL_FAILED, {
          upsell: 'DAY_PRIME_RX_UPSELL',
          error: error.response?.data.message || error.message,
        });
      })
      .finally(() => setProcessing(false));
  };

  return (
    <>
      <section className="cur-mb-25">
        <Typography size="h2">
          Target your skin goals <span className="cur-text-brand">24/7</span> by
          adding DayPrime
          <sup>Rx</sup> to your routine
        </Typography>
        <div className="cur-mt-4">
          <ProductCard />
        </div>
        <Typography className="cur-mt-6" size="h2">
          <span className="cur-text-brand">Treat and prime</span> at the same
          time
        </Typography>
        <Image
          src={beforeAndAfterImage}
          alt="DayPrime Rx before and after"
          width={358}
          height={358}
          className="cur-mx-auto cur-mt-4"
        />
        <ul className="cur-mt-4 cur-space-y-4 cur-bg-white cur-p-4">
          {features.map((feature, index) => (
            <li key={index} className="cur-flex cur-items-start cur-space-x-4">
              <CircleCheckIcon className="cur-w-6 cur-h-6 cur-flex-shrink-0 cur-text-surface-brand-accent" />
              <Typography size="body">{feature}</Typography>
            </li>
          ))}
        </ul>
        <Typography className="cur-mt-6" size="meta">
          After your 30-day trial ends, your recurring subscription box will be
          $29.90 more each billing cycle with Day Prime
          <sup>Rx</sup> (60-day supply).
        </Typography>
        <Typography className="cur-mt-4" size="meta">
          Rx products are subject to consultation. If your dermatology provider
          cannot prescribe one due to any reason, it will be removed, and the
          cost of your subscription may be lower.
        </Typography>
      </section>

      <div
        className={clsx(
          'cur-fixed cur-bottom-0 cur-left-0 cur-w-full',
          !open && 'cur-z-[999999]',
        )}
      >
        <div className="cur-bg-white cur-flex cur-justify-center cur-items-center cur-space-x-2 cur-p-4">
          <Button
            onClick={() => {
              setProcessing(true);
              trackEvent(AnalyticsEvent.UPSELL_DECLINED, {
                upsell: 'DAY_PRIME_RX_UPSELL',
              });
              post(
                '/api/patient/upsells',
                { upsell: 'DAY_PRIME_RX_UPSELL', accepted: false },
                { headers: jsonHeaders },
              )
                .catch(() => {})
                .finally(() => onNext());
            }}
            className="cur-w-[155px]"
            buttonType="secondary"
            disabled={processing}
          >
            Skip
          </Button>
          <Button
            loading={processing}
            onClick={acceptUpsell}
            className="cur-w-[155px]"
          >
            Add for free
          </Button>
        </div>
      </div>
    </>
  );
};
